import { ROUTES } from '@/routes/routes'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'
import { useRouter } from '@/routing/navigation'
import { chatRoomsApi, populationsApi, publicApi, AudienceSummary } from '@/lib/api'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import { RoomVisibility, StudyContext } from '@digital-twins/shared-types'
import type { StimulusImage, StimulusImagePolicy } from '@digital-twins/shared-types'
import type { AiFilterCondition } from '@/types/components/audience-filter-modal'
import type { PopulationFilter, FilterCondition } from '@/types/routes/test-results'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { detectTrialLimitError } from '@/lib/billing/trial-limits'
import { useTrialChatRoomLimit } from '@/lib/hooks/use-trial-limits'
import i18n, { defaultContentLanguageFromUi } from '@/i18n'
import { normalizeDashes } from '@/lib/utils/normalize-dashes'
import { resolvePickedTwins } from './pickedTwins'

function matchesTwinFilter(
  attrs: Record<string, unknown>,
  filter: PopulationFilter,
  columnNameToAttrKey: Record<string, string>,
): boolean {
  const matchCondition = (cond: FilterCondition): boolean => {
    const key = cond.field
    const attrKey = columnNameToAttrKey[key] ?? key
    const resolvedKey = `_resolved_${attrKey}`
    const resolved = attrs[resolvedKey] ?? attrs[`_resolved_${key}`]
    const raw: unknown = resolved ?? attrs[attrKey] ?? attrs[key]
    if (raw === null || raw === undefined) return false

    const leafValues = typeof raw === 'object' && !Array.isArray(raw)
      ? Object.values(raw as Record<string, unknown>).map((lv) => String(lv ?? '').trim().toLowerCase())
      : Array.isArray(raw)
        ? (raw as Array<unknown>).map((lv) => String(lv ?? '').trim().toLowerCase())
        : [String(raw).trim().toLowerCase()]

    const allowed = Array.isArray(cond.value) ? cond.value : [cond.value]
    return leafValues.some((lv) => allowed.some((v) => v.toLowerCase() === lv))
  }

  const matchNode = (node: PopulationFilter): boolean => {
    if (node.logic === 'OR') {
      return node.conditions.some((c) =>
        'field' in c ? matchCondition(c as FilterCondition) : matchNode(c as PopulationFilter),
      )
    }
    return node.conditions.every((c) =>
      'field' in c ? matchCondition(c as FilterCondition) : matchNode(c as PopulationFilter),
    )
  }

  return matchNode(filter)
}

type ComposeStep = 'configure' | 'preview'

interface InitialTwin {
  personaId: string
  populationId: string
  personaName?: string
  imageUrl?: string | null
}

interface TwinSuggestion {
  personaId: string
  personaName: string
  imageUrl: string | null
  bio?: string | null
  isReady: boolean
  attributes: Record<string, unknown>
  rawAttributes?: Record<string, unknown>
  audienceTagNames?: Array<string>
  resolvedIdentity?: {
    display_name?: string
    country?: string
    gender?: string
    age_range?: string
  } | null
}

export function mapPickerPersonaToTwinSuggestion(p: {
  personaId: string
  personaName: string
  imageUrl?: string | null
  displayBio?: string | null
  isReady?: boolean
  displayAttributes?: Record<string, string>
  attributes?: Record<string, unknown>
  rawAttributes?: Record<string, unknown>
  audienceTagNames?: Array<string>
  resolvedIdentity?: TwinSuggestion['resolvedIdentity']
}): TwinSuggestion {
  return {
    personaId: p.personaId,
    personaName: p.personaName,
    imageUrl: p.imageUrl ?? null,
    bio: p.displayBio ?? null,
    isReady: p.isReady ?? !!p.imageUrl,
    attributes: p.displayAttributes ?? p.attributes ?? {},
    rawAttributes: p.rawAttributes,
    audienceTagNames: p.audienceTagNames,
    resolvedIdentity: p.resolvedIdentity ?? null,
  }
}

/** Portrait URLs shown in the picker, keyed by persona token for createRoom fallback. */
export function buildParticipantImages(
  selectedIds: Array<string>,
  twins: Array<TwinSuggestion>,
  cache: Map<string, TwinSuggestion>,
): Record<string, string> | undefined {
  const images: Record<string, string> = {}
  for (const id of selectedIds) {
    const live = twins.find((t) => t.personaId === id)
    const cached = cache.get(id)
    const url = (live?.imageUrl ?? cached?.imageUrl)?.trim()
    if (url) images[id] = url
  }
  return Object.keys(images).length > 0 ? images : undefined
}

/** Audience bios shown in the picker, keyed by persona token for createRoom snapshot. */
export function buildParticipantBios(
  selectedIds: Array<string>,
  twins: Array<TwinSuggestion>,
  cache: Map<string, TwinSuggestion>,
): Record<string, string> | undefined {
  const bios: Record<string, string> = {}
  for (const id of selectedIds) {
    const live = twins.find((t) => t.personaId === id)
    const cached = cache.get(id)
    const bio = (live?.bio ?? cached?.bio)?.trim()
    if (bio) bios[id] = normalizeDashes(bio)
  }
  return Object.keys(bios).length > 0 ? bios : undefined
}

function resolveComposeDefaultLanguage(
  initialLanguage: string | null | undefined,
  audience: AudienceSummary | null,
): string {
  const explicit = initialLanguage?.trim()
  if (explicit) {
    return explicit
  }
  const audienceLanguage = audience?.language?.trim()
  if (audienceLanguage) {
    return audienceLanguage
  }
  return defaultContentLanguageFromUi()
}

interface UseComposeModalOptions {
  open: boolean
  initialTwin?: InitialTwin | null
  initialTwinIds?: Array<string>
  initialTopics?: string
  sourceThreadToken?: string
  /** The Assistant launch card this room is started from. */
  sourceMessageToken?: string
  initialAudienceId?: string | null
  initialMessage?: string | null
  groupMode?: boolean
  lockedAudienceId?: string | null
  studyContext?: StudyContext | null
  /**
   * Experiment-report creatives: persist as room stimuli and attach images only
   * on every turn within the bounded permitted media context.
   */
  stimulusImages?: Array<StimulusImage> | null
  stimulusImagePolicy?: StimulusImagePolicy | null
  restrictedPersonaIds?: Array<string> | null
  /** Pass study respondents directly to skip the population API fetch entirely */
  studyRespondents?: Array<TwinSuggestion> | null
  initialLanguage?: string | null
  /** Pre-loaded audience metadata when opening from audience detail (before populations list resolves) */
  initialAudienceSnapshot?: AudienceSummary | null
  /**
   * Run against a shared study link instead of the authenticated APIs: twins,
   * filters and room creation all go through `/public/reports/:shareToken/*`
   * with the guest's OTP session. The parent handles the created room (there is
   * no `/chats/:token` route for a guest), hence `onRoomCreated`.
   */
  guestScope?: { shareToken: string } | null
  onRoomCreated?: (roomToken: string) => void
}

interface UseComposeModalReturn {
  step: ComposeStep
  goToPreview: () => void
  goBack: () => void
  audiences: Array<AudienceSummary>
  audiencesLoading: boolean
  selectedAudienceId: string | null
  setSelectedAudienceId: (id: string | null) => void
  selectedAudience: AudienceSummary | null
  isAudienceLocked: boolean
  selectedTwins: Array<TwinSuggestion>
  selectedTwinIds: Array<string>
  toggleTwin: (personaId: string) => void
  twins: Array<TwinSuggestion>
  twinsLoading: boolean
  twinsLoadingMore: boolean
  twinsHasMore: boolean
  loadMoreTwins: () => void
  twinSearch: string
  setTwinSearch: (query: string) => void
  twinFilters: Record<string, Array<string>>
  setTwinFilters: (filters: Record<string, Array<string>>) => void
  aiChips: Array<AiFilterCondition>
  handleAiApplyFilters: (filters: Record<string, Array<string>>, chips: Array<AiFilterCondition>, mode: 'replace' | 'add') => void
  handleRemoveAiChip: (column: string) => void
  handleOpenFilterModalPrefilled: (selections: Record<string, Array<string>>) => void
  twinSelectionTags: Record<string, Array<{ chip: string; displayQuestion: string; displayAnswer: string }>>
  handleFiltersChange: (filters: Record<string, Array<string>>, rawFilter?: PopulationFilter | null) => void
  rawPopulationFilter: PopulationFilter | null
  populationColumns: Array<{ name: string; uniqueValues: Array<string>; displayLabel?: string; canonicalId?: string | null; queryKey?: string }>
  columnMetadataTotalRows: number
  populationColumnGroups: Array<{ groupId: string; memberColumns: Array<string>; groupType: 'binary' | 'multiselect'; rawLabel: string | null; responseEncoding: string | null; rawGroupType: string | null; detectionRoute: string | null; source: 'activeGroups' | 'phase0' }>
  columnNameToAttrKey: Record<string, string>
  showFilters: boolean
  setShowFilters: (show: boolean) => void
  language: string
  setLanguage: (code: string) => void
  visibility: RoomVisibility
  setVisibility: (vis: RoomVisibility) => void
  isCreating: boolean
  error: string | null
  canCreate: boolean
  canPreview: boolean
  hasUnreadyTwins: boolean
  createRoom: () => Promise<void>
  isFocusGroupRoom: boolean
  trialRoomLimitOpen: boolean
  closeTrialRoomLimit: () => void
}

export function useComposeModal({ open, initialTwin, initialTwinIds, initialTopics, sourceThreadToken, sourceMessageToken, initialAudienceId, initialMessage, groupMode = false, lockedAudienceId, studyContext, stimulusImages, stimulusImagePolicy, restrictedPersonaIds, studyRespondents, initialLanguage, initialAudienceSnapshot, guestScope, onRoomCreated }: UseComposeModalOptions): UseComposeModalReturn {
  const router = useRouter()
  const guestShareToken = guestScope?.shareToken ?? null
  // Guests have no audience library — the share pins exactly one audience, and
  // the caller supplies it as `initialAudienceSnapshot`.
  const { data: populations, isLoading: audiencesLoading } = useSWR(
    guestShareToken ? null : 'populations',
    () => populationsApi.list({ limit: 500 }),
  )

  const [step, setStep] = useState<ComposeStep>('configure')
  const [selectedAudienceId, setSelectedAudienceId] = useState<string | null>(null)
  const [selectedTwinIds, setSelectedTwinIds] = useState<Array<string>>([])
  const [twins, setTwins] = useState<Array<TwinSuggestion>>([])
  const [twinsLoading, setTwinsLoading] = useState(false)
  const [twinsLoadingMore, setTwinsLoadingMore] = useState(false)
  const [twinsHasMore, setTwinsHasMore] = useState(false)
  const [twinsPage, setTwinsPage] = useState(1)
  const [twinSearch, setTwinSearch] = useState('')
  const [twinFilters, setTwinFilters] = useState<Record<string, Array<string>>>({})
  const [rawPopulationFilter, setRawPopulationFilter] = useState<PopulationFilter | null>(null)
  const [aiChips, setAiChips] = useState<Array<AiFilterCondition>>([])
  const [twinSelectionTags, setTwinSelectionTags] = useState<
    Record<string, Array<{ chip: string; displayQuestion: string; displayAnswer: string }>>
  >({})
  const [populationColumns, setPopulationColumns] = useState<Array<{ name: string; uniqueValues: Array<string>; displayLabel?: string; canonicalId?: string | null; queryKey?: string }>>([])
  const [columnMetadataTotalRows, setColumnMetadataTotalRows] = useState(0)
  const [populationColumnGroups, setPopulationColumnGroups] = useState<Array<{ groupId: string; memberColumns: Array<string>; groupType: 'binary' | 'multiselect'; rawLabel: string | null; responseEncoding: string | null; rawGroupType: string | null; detectionRoute: string | null; source: 'activeGroups' | 'phase0' }>>([])


  const columnNameToAttrKey = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {}
    for (const col of populationColumns) {
      if (col.queryKey && col.queryKey !== col.name) {
        map[col.name] = col.queryKey
      }
    }
    // The AI backend exposes resolved identity columns with canonical names
    // (std_dem_gender, std_dem_age, std_geo_country) but twin attributes store
    // resolved demographics under _resolved_gender, _resolved_age, _resolved_country.
    // Bridge the naming gap so client-side filtering works for AI conditions.
    const CANONICAL_TO_RESOLVED: Record<string, string> = {
      std_dem_gender: '_resolved_gender',
      std_dem_age: '_resolved_age',
      std_geo_country: '_resolved_country',
    }
    for (const [canonical, resolved] of Object.entries(CANONICAL_TO_RESOLVED)) {
      if (!map[canonical]) {
        map[canonical] = resolved
      }
    }
    return map
  }, [populationColumns])

  const [showFilters, setShowFilters] = useState(false)
  const [language, setLanguage] = useState(() =>
    resolveComposeDefaultLanguage(initialLanguage, null),
  )
  const [visibility, setVisibility] = useState<RoomVisibility>('private')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [trialRoomLimitOpen, setTrialRoomLimitOpen] = useState(false)

  const { limitReached: trialRoomLimitReached } = useTrialChatRoomLimit()

  const selectedTwinCacheRef = useRef<Map<string, TwinSuggestion>>(new Map())

  const readyAudiences = useMemo(
    () => (populations ?? []).filter((p) => p.status === 'ready') as Array<AudienceSummary>,
    [populations],
  )

  const audiencesForPicker = useMemo(() => {
    if (!initialAudienceSnapshot) {
      return readyAudiences
    }
    if (readyAudiences.some((a) => a.id === initialAudienceSnapshot.id)) {
      return readyAudiences
    }
    return [initialAudienceSnapshot, ...readyAudiences]
  }, [readyAudiences, initialAudienceSnapshot])

  const selectedAudience = useMemo(() => {
    if (!selectedAudienceId) {
      return null
    }
    const fromReady = readyAudiences.find((a) => a.id === selectedAudienceId)
    if (fromReady) {
      return fromReady
    }
    const fromAll = (populations ?? []).find((a) => a.id === selectedAudienceId)
    if (fromAll) {
      return fromAll as AudienceSummary
    }
    if (initialAudienceSnapshot?.id === selectedAudienceId) {
      return initialAudienceSnapshot
    }
    return null
  }, [readyAudiences, populations, selectedAudienceId, initialAudienceSnapshot])

  const selectedTwins = useMemo(
    () => selectedTwinIds.map((id) => selectedTwinCacheRef.current.get(id)).filter(Boolean) as Array<TwinSuggestion>,
    [selectedTwinIds],
  )

  useEffect(() => {
    if (!open || initialLanguage?.trim()) {
      return
    }
    setLanguage(resolveComposeDefaultLanguage(initialLanguage, selectedAudience))
  }, [open, initialLanguage, selectedAudience])

  const goToPreview = useCallback(() => setStep('preview'), [])
  const goBack = useCallback(() => setStep('configure'), [])

  useEffect(() => {
    if (!open) return
    track(AnalyticsEvents.COMPOSE_MODAL_OPENED)
  }, [open])

  useEffect(() => {
    if (!open) {
      setStep('configure')
      setSelectedAudienceId(null)
      setSelectedTwinIds([])
      setTwins([])
      setTwinSearch('')
      setError(null)
      setIsCreating(false)
      selectedTwinCacheRef.current.clear()
      return
    }

    setLanguage(resolveComposeDefaultLanguage(initialLanguage, null))

    if (initialTwinIds && initialAudienceId) {
      setSelectedAudienceId(initialAudienceId)
      setSelectedTwinIds(initialTwinIds)
      if (!initialLanguage?.trim()) {
        const audience =
          readyAudiences.find((a) => a.id === initialAudienceId) ??
          (initialAudienceSnapshot?.id === initialAudienceId ? initialAudienceSnapshot : null)
        setLanguage(resolveComposeDefaultLanguage(initialLanguage, audience))
      }
      return
    }

    if (initialTwin) {
      setSelectedAudienceId(initialTwin.populationId)
      if (initialTwin.personaId && initialTwin.personaId.length > 0) {
        setSelectedTwinIds([initialTwin.personaId])
        selectedTwinCacheRef.current.set(initialTwin.personaId, {
          personaId: initialTwin.personaId,
          personaName: initialTwin.personaName ?? '',
          imageUrl: initialTwin.imageUrl ?? null,
          isReady: !!initialTwin.imageUrl,
          attributes: {},
          resolvedIdentity: null,
        })
      }
      return
    }

    if (lockedAudienceId) {
      setSelectedAudienceId(lockedAudienceId)
      return
    }

    if (initialAudienceId) {
      setSelectedAudienceId(initialAudienceId)
      return
    }

    setSelectedAudienceId(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTwin, initialTwinIds, initialAudienceId, lockedAudienceId, groupMode])

  // Resolve exact tokens into the same selection cache the picker and create path use, through
  // the picker's own rows: a pick is any twin of the audience, and most are not featured.
  // Aborting on close/audience changes prevents late results populating another room.
  useEffect(() => {
    if (!open || guestShareToken || !initialAudienceId || !initialTwinIds?.length || selectedAudienceId !== initialAudienceId) return
    const controller = new AbortController()
    void resolvePickedTwins<Parameters<typeof mapPickerPersonaToTwinSuggestion>[0]>(
      initialTwinIds,
      async (ids) => (await populationsApi.getPersonasForPicker(initialAudienceId, { contextPersonaIds: ids, limit: ids.length })).personas,
    ).then((rows) => {
      if (controller.signal.aborted) {
        return
      }
      for (const row of rows) {
        if (row.detail) {
          selectedTwinCacheRef.current.set(row.personaId, mapPickerPersonaToTwinSuggestion(row.detail))
          continue
        }
        if (!selectedTwinCacheRef.current.has(row.personaId)) {
          selectedTwinCacheRef.current.set(row.personaId, {
            personaId: row.personaId,
            personaName: i18n.t('threads.sessions.selectedTwin', { ns: 'app' }),
            imageUrl: null,
            isReady: false,
            attributes: {},
            resolvedIdentity: null,
          })
        }
      }
      setSelectedTwinIds((current) => [...current])
    })
    return () => controller.abort()
  }, [open, guestShareToken, initialAudienceId, initialTwinIds, selectedAudienceId])

  const TWINS_PAGE_SIZE = 40

  useEffect(() => {
    if (!open || !selectedAudienceId) {
      setPopulationColumns([])
      setColumnMetadataTotalRows(0)
      return
    }

    // Study mode: studyRespondents !== undefined means the caller owns the respondent list.
    // - null  → still resolving; defer until the array arrives
    // - Array → use the population's canonical column metadata (same as Conversations filter)
    //           but override totalRows with the study respondent count so counts stay scoped.
    if (studyRespondents !== undefined && !studyRespondents) return

    // Capture synchronously before any await so the async closure never reads a stale value.
    const studyCount = Array.isArray(studyRespondents) ? studyRespondents.length : undefined

    let cancelled = false
    async function loadColumns() {
      try {
        const meta = guestShareToken
          ? await publicApi.getAudienceColumnMetadata(guestShareToken)
          : await populationsApi.getColumnMetadata(selectedAudienceId!)
        if (cancelled) return
        setPopulationColumns(
          meta.columns.map((c) => ({
            name: c.name,
            uniqueValues: c.uniqueValues || [],
            displayLabel: c.displayLabel || c.name,
            canonicalId: c.canonicalId ?? null,
            queryKey: c.queryKey,
          }))
        )
        // The guest endpoint returns columns only — no group metadata.
        setPopulationColumnGroups(
          (meta as { groups?: typeof populationColumnGroups }).groups ?? [],
        )
        // In study mode override totalRows with the study respondent count
        setColumnMetadataTotalRows(studyCount !== undefined ? studyCount : (meta.totalRows ?? 0))
      } catch {
        if (!cancelled) {
          setPopulationColumns([])
          setColumnMetadataTotalRows(0)
        }
      }
    }
    loadColumns()
    return () => { cancelled = true }
  }, [open, selectedAudienceId, studyRespondents, guestShareToken])

  // When the caller provides study respondents (studyRespondents !== undefined), own the twin list entirely.
  useEffect(() => {
    if (!open || !selectedAudienceId || studyRespondents === undefined) return

    // null means study mode but still resolving — show loading skeleton
    if (studyRespondents === null) {
      setTwinsLoading(true)
      setTwins([])
      return
    }

    let source = [...studyRespondents]

    // Apply client-side search filter
    if (twinSearch) {
      const needle = twinSearch.toLowerCase()
      source = source.filter((t) => t.personaName.toLowerCase().includes(needle))
    }

    if (rawPopulationFilter) {
      source = source.filter((t) => {
        const attrs = t.rawAttributes ?? t.attributes ?? {}
        return matchesTwinFilter(attrs, rawPopulationFilter, columnNameToAttrKey)
      })
    } else {
      const activeFilters = Object.entries(twinFilters).filter(([, vals]) => vals && vals.length > 0)
      if (activeFilters.length > 0) {
        source = source.filter((t) => {
          const attrs = t.rawAttributes ?? t.attributes ?? {}
          return activeFilters.every(([key, allowed]) => {
            const attrKey = columnNameToAttrKey[key] ?? key
            const resolvedKey = `_resolved_${attrKey}`
            const resolved = attrs[resolvedKey] ?? attrs[`_resolved_${key}`]
            const raw: unknown = resolved ?? attrs[attrKey] ?? attrs[key]
            if (raw === null || raw === undefined) return false
            const leafValues = typeof raw === 'object' && !Array.isArray(raw)
              ? Object.values(raw as Record<string, unknown>).map((lv) => String(lv ?? '').trim().toLowerCase())
              : Array.isArray(raw)
                ? (raw as Array<unknown>).map((lv) => String(lv ?? '').trim().toLowerCase())
                : [String(raw).trim().toLowerCase()]
            return leafValues.some((lv) => allowed.some((v) => v.toLowerCase() === lv))
          })
        })
      }
    }

    // The initial twin is selected by its persona id (`initialTwin.personaId`, required).
    const withImage = source.filter((t) => t.imageUrl).sort(() => Math.random() - 0.5)
    const withoutImage = source.filter((t) => !t.imageUrl).sort(() => Math.random() - 0.5)
    setTwins([...withImage, ...withoutImage])
    setTwinsHasMore(false)
    setTwinsLoading(false)
  }, [open, selectedAudienceId, studyRespondents, twinSearch, twinFilters, rawPopulationFilter, columnNameToAttrKey])

  useEffect(() => {
    if (!open || !selectedAudienceId) {
      setTwins([])
      return
    }

    // Skip population API fetch when in study mode (studyRespondents !== undefined),
    // whether loading (null) or already loaded (array).
    if (studyRespondents !== undefined) return

    let cancelled = false
    setTwinsLoading(true)
    setTwinsPage(1)

    const filtersToApply: Record<string, Array<string>> = {}
    if (!rawPopulationFilter) {
      for (const [key, values] of Object.entries(twinFilters)) {
        if (values && values.length > 0) filtersToApply[key] = values
      }
    }

    async function loadTwins() {
      try {
        const query = {
          page: 1,
          limit: TWINS_PAGE_SIZE,
          search: twinSearch || undefined,
          filters: Object.keys(filtersToApply).length > 0 ? filtersToApply : undefined,
        }
        const response = guestShareToken
          ? await publicApi.getPersonasForPicker(guestShareToken, query)
          : await populationsApi.getPersonasForPicker(selectedAudienceId!, {
              ...query,
              populationFilter: rawPopulationFilter ?? undefined,
            })
        if (cancelled) return
        const mapped: Array<TwinSuggestion> = response.personas.map(mapPickerPersonaToTwinSuggestion)

        // The initial twin is selected by its persona id (`initialTwin.personaId`, required).
        const withImage = mapped.filter((t) => t.imageUrl).sort(() => Math.random() - 0.5)
        const withoutImage = mapped.filter((t) => !t.imageUrl).sort(() => Math.random() - 0.5)
        let sortedTwins = [...withImage, ...withoutImage]

        if (restrictedPersonaIds && restrictedPersonaIds.length > 0) {
          const restrictedSet = new Set(restrictedPersonaIds)
          sortedTwins = sortedTwins.filter((t) => restrictedSet.has(t.personaId))
        }

        setTwins(sortedTwins)
        for (const twin of sortedTwins) {
          if (selectedTwinCacheRef.current.has(twin.personaId)) {
            selectedTwinCacheRef.current.set(twin.personaId, twin)
          }
        }
        setTwinsHasMore(!restrictedPersonaIds && response.personas.length >= TWINS_PAGE_SIZE)
      } catch {
        if (!cancelled) setTwins([])
      } finally {
        if (!cancelled) setTwinsLoading(false)
      }
    }

    loadTwins()
    return () => { cancelled = true }
  }, [open, selectedAudienceId, twinSearch, twinFilters, rawPopulationFilter, restrictedPersonaIds, studyRespondents, guestShareToken])

  const MAX_TWINS = 10

  const loadMoreTwins = useCallback(async () => {
    if (!selectedAudienceId || twinsLoadingMore || !twinsHasMore) return
    setTwinsLoadingMore(true)
    const nextPage = twinsPage + 1
    const filtersToApply: Record<string, Array<string>> = {}
    if (!rawPopulationFilter) {
      for (const [key, values] of Object.entries(twinFilters)) {
        if (values && values.length > 0) filtersToApply[key] = values
      }
    }
    try {
      const query = {
        page: nextPage,
        limit: TWINS_PAGE_SIZE,
        search: twinSearch || undefined,
        filters: Object.keys(filtersToApply).length > 0 ? filtersToApply : undefined,
      }
      const response = guestShareToken
        ? await publicApi.getPersonasForPicker(guestShareToken, query)
        : await populationsApi.getPersonasForPicker(selectedAudienceId, {
            ...query,
            populationFilter: rawPopulationFilter ?? undefined,
          })
      const mapped: Array<TwinSuggestion> = response.personas.map(mapPickerPersonaToTwinSuggestion)
      const withImage = mapped.filter((t) => t.imageUrl).sort(() => Math.random() - 0.5)
      const withoutImage = mapped.filter((t) => !t.imageUrl).sort(() => Math.random() - 0.5)
      setTwins((prev) => [...prev, ...withImage, ...withoutImage])
      setTwinsPage(nextPage)
      setTwinsHasMore(response.personas.length >= TWINS_PAGE_SIZE)
    } catch {
      setTwinsHasMore(false)
    } finally {
      setTwinsLoadingMore(false)
    }
  }, [selectedAudienceId, twinsPage, twinsLoadingMore, twinsHasMore, twinSearch, twinFilters, rawPopulationFilter, guestShareToken])

  const handleAudienceChange = useCallback((id: string | null) => {
    setSelectedAudienceId(id)
    setSelectedTwinIds([])
    setTwinSearch('')
    setTwinFilters({})
    setRawPopulationFilter(null)
    setShowFilters(false)
    setError(null)
    setAiChips([])
    setTwinSelectionTags({})
    selectedTwinCacheRef.current.clear()
  }, [])

  const toggleTwin = useCallback(
    (personaId: string) => {
      setSelectedTwinIds((prev) => {
        if (prev.includes(personaId)) {
          selectedTwinCacheRef.current.delete(personaId)
          setTwinSelectionTags((tags) => {
            const next = { ...tags }
            delete next[personaId]
            return next
          })
          return prev.filter((id) => id !== personaId)
        }
        if (prev.length >= MAX_TWINS) return prev
        const twin = twins.find((t) => t.personaId === personaId)
        if (twin) selectedTwinCacheRef.current.set(personaId, twin)
        if (aiChips.length > 0) {
          setTwinSelectionTags((tags) => ({
            ...tags,
            [personaId]: aiChips.map(({ chip, displayQuestion, displayAnswer }) => ({
              chip,
              displayQuestion,
              displayAnswer,
            })).slice(0, 8),
          }))
        }
        return [...prev, personaId]
      })
    },
    [aiChips, twins],
  )

  const handleAiApplyFilters = useCallback(
    (
      filters: Record<string, Array<string>>,
      chips: Array<AiFilterCondition>,
      mode: 'replace' | 'add',
    ) => {
      if (mode === 'add') {
        setTwinFilters((prev) => {
          const merged = { ...prev }
          for (const [col, vals] of Object.entries(filters)) {
            const existing = merged[col] ?? []
            merged[col] = Array.from(new Set([...existing, ...vals]))
          }
          return merged
        })
        setAiChips((prev) => {
          const updatedMap = new Map(prev.map((c) => [c.column, c]))
          for (const chip of chips) {
            const existing = updatedMap.get(chip.column)
            if (existing) {
              updatedMap.set(chip.column, {
                ...chip,
                values: Array.from(new Set([...existing.values, ...chip.values])),
              })
            } else {
              updatedMap.set(chip.column, chip)
            }
          }
          return Array.from(updatedMap.values())
        })
      } else {
        setTwinFilters(filters)
        setAiChips(chips)
      }
    },
    [],
  )

  const handleRemoveAiChip = useCallback(
    (column: string) => {
      setAiChips((prev) => prev.filter((c) => c.column !== column))
      setTwinFilters((prev) => {
        const next = { ...prev }
        delete next[column]
        return next
      })
    },
    [],
  )

  const handleOpenFilterModalPrefilled = useCallback(
    (selections: Record<string, Array<string>>) => {
      setTwinFilters(selections)
      setAiChips([])
    },
    [],
  )

  const handleFiltersChange = useCallback(
    (filters: Record<string, Array<string>>, rawFilter?: PopulationFilter | null) => {
      setTwinFilters(filters)
      setRawPopulationFilter(rawFilter ?? null)
      if (Object.keys(filters).length === 0 && !rawFilter) {
        setAiChips([])
      }
    },
    [],
  )

  const minSelectedTwins = groupMode ? 2 : 1
  const canCreate = selectedAudienceId !== null && selectedTwinIds.length >= minSelectedTwins && !isCreating
  const canPreview = selectedAudienceId !== null && selectedTwinIds.length > 0

  const hasUnreadyTwins = useMemo(
    () => selectedTwins.some((t) => !t.isReady),
    [selectedTwins],
  )

  const isFocusGroupRoom = selectedTwinIds.length > 1

  const createRoom = useCallback(async () => {
    if (!selectedAudienceId || selectedTwinIds.length === 0) return
    if (trialRoomLimitReached) {
      setTrialRoomLimitOpen(true)
      return
    }
    setIsCreating(true)
    setError(null)

    try {
      const participantImages = buildParticipantImages(
        selectedTwinIds,
        twins,
        selectedTwinCacheRef.current,
      )

      const participantBios = buildParticipantBios(
        selectedTwinIds,
        twins,
        selectedTwinCacheRef.current,
      )

      if (guestShareToken) {
        // The server owns study grounding, stimuli and ownership for guest
        // rooms — the guest only chooses who is in the room and how it talks.
        const guestRoom = await publicApi.createRoom(guestShareToken, {
          participantIds: selectedTwinIds,
          settings: { language },
          ...(Object.keys(twinSelectionTags).length > 0 ? { participantTags: twinSelectionTags } : {}),
          ...(participantImages ? { participantImages } : {}),
          ...(participantBios ? { participantBios } : {}),
        })

        track(AnalyticsEvents.ROOM_CREATED, {
          type: 'focus_group',
          participant_count: selectedTwinIds.length,
          audience_id: selectedAudienceId,
          language,
          guest: true,
        })

        setIsCreating(false)
        onRoomCreated?.(guestRoom.token)
        return
      }

      const room = await chatRoomsApi.create({
        populationId: selectedAudienceId,
        participantPersonaIds: selectedTwinIds,
        settings: {
          language,
          visibility,
          ...(initialTopics ? { topics: initialTopics } : {}),
          ...(sourceThreadToken ? { sourceThreadToken } : {}),
          ...(sourceThreadToken && sourceMessageToken ? { sourceMessageToken } : {}),
          ...(stimulusImages && stimulusImages.length > 0
            ? {
                stimulusImages,
                ...(stimulusImagePolicy
                  ? { stimulusImagePolicy }
                  : {}),
              }
            : {}),
        },
        ...(studyContext ? { studyContext } : {}),
        ...(Object.keys(twinSelectionTags).length > 0 ? { participantTags: twinSelectionTags } : {}),
        ...(participantImages ? { participantImages } : {}),
        ...(participantBios ? { participantBios } : {}),
      })

      track(AnalyticsEvents.ROOM_CREATED, {
        type: selectedTwinIds.length === 1 ? 'interview' : 'focus_group',
        participant_count: selectedTwinIds.length,
        audience_id: selectedAudienceId,
        language,
      })

      setIsCreating(false)
      if (onRoomCreated) onRoomCreated(room.token)
      else router.push(ROUTES.chatRoom(room.token), { state: { initialMessage: initialMessage || undefined } })
    } catch (err) {
      // Trial room cap reached — surface the upgrade modal before the chat
      // starts instead of a generic inline error.
      if (detectTrialLimitError(err) === 'rooms') {
        setTrialRoomLimitOpen(true)
        setIsCreating(false)
        return
      }
      const blocked = blockedFeatureMessage(err)
      const message = blocked
        ?? (err instanceof Error ? err.message : i18n.t('compose.createFailed', { ns: 'app' }))
      setError(message)
      setIsCreating(false)
    }
  }, [selectedAudienceId, selectedTwinIds, twins, language, visibility, router, initialMessage, studyContext, stimulusImages, stimulusImagePolicy, twinSelectionTags, trialRoomLimitReached, guestShareToken, onRoomCreated, initialTopics, sourceThreadToken, sourceMessageToken])

  return {
    step,
    goToPreview,
    goBack,
    audiences: audiencesForPicker,
    audiencesLoading,
    selectedAudienceId,
    setSelectedAudienceId: handleAudienceChange,
    selectedAudience,
    isAudienceLocked: !!lockedAudienceId,
    selectedTwins,
    selectedTwinIds,
    toggleTwin,
    twins,
    twinsLoading,
    twinsLoadingMore,
    twinsHasMore,
    loadMoreTwins,
    twinSearch,
    setTwinSearch,
    twinFilters,
    setTwinFilters,
    aiChips,
    handleAiApplyFilters,
    handleRemoveAiChip,
    handleOpenFilterModalPrefilled,
    twinSelectionTags,
    handleFiltersChange,
    rawPopulationFilter,
    populationColumns,
    columnMetadataTotalRows,
    populationColumnGroups,
    columnNameToAttrKey,
    showFilters,
    setShowFilters,
    language,
    setLanguage,
    visibility,
    setVisibility,
    isCreating,
    error,
    canCreate,
    canPreview,
    hasUnreadyTwins,
    createRoom,
    isFocusGroupRoom,
    trialRoomLimitOpen,
    closeTrialRoomLimit: () => setTrialRoomLimitOpen(false),
  }
}

export type { InitialTwin, TwinSuggestion }
